import { TextField, PrimaryButton, ProgressIndicator, DefaultButton } from "@fluentui/react";
import { Authentication } from "../../common/views/Authentication";
import {
    defaultButtonStyles, primaryButtonStyles, styles, textFieldStyles
} from "../../common/views/DefaultStyles";
import { Utils } from "../../common/views/Utils";
import { AccountModel, AccountProvider, ForgotPasswordModel } from "../models/AccountTypes";
import { useConfirmAccountVM } from "../viewmodels/ConfirmAccountVM";
import { accountCallback } from "../viewmodels/LoginVM";
import { ConfirmAccountView } from "./ConfirmAccountView";
import { AccountOptionView } from "./AccountOptionView";
import PhoneView from "./PhoneView";

type ForgotPasswordProps = {
    form: ForgotPasswordModel;
    errorMessages: any;
    loading: boolean;
    onValueChange: (field: keyof ForgotPasswordModel, value: any) => void;
    submit: (callback: accountCallback) => void;
    showConfirm: boolean;
    setShowConfirm: (value: boolean) => void;
}

export function ForgotPasswordView({
    form, errorMessages, onValueChange, submit, loading, showConfirm, setShowConfirm
}: ForgotPasswordProps) {
    const isMobile = Utils.isMobile();
    const id = "login-form-wrapper" + (isMobile ? "-mobile" : "");
    const width6 = Utils.getColumnWidth(6);
    const width12 = Utils.getColumnWidth(12);

    const confirmVM = useConfirmAccountVM(true);

    const onSubmit = () => {
        Authentication.setCountryCode(form.countryCode);
        Authentication.setPhoneNo(form.phoneNo);
        Authentication.setEmail(form.email ?? "");

        submit(onSubmitResult);
    }

    const onSubmitResult = (account: AccountModel) => {
        if (!account.isConfirmed) {
            confirmVM.sendCode(account, form.isByEmail);
            setShowConfirm(true);
        } else {
            goHome();
        }
    }

    const goHome = () => {
        window.open("/", "_self");
    }

    const phoneView = () => {
        return <>
            <PhoneView
                showTelegram={false}
                form={form}
                errorMessages={errorMessages}
                onValueChange={onValueChange}
                isRequired={true} />
        </>;
    }

    const emailInput = () => {
        return <div className="ms-Grid-row left">
            <div className={width12}>
                <TextField
                    label="Email"
                    defaultValue={form.email}
                    required
                    type="Email"
                    errorMessage={errorMessages.email}
                    onChange={(_, v) => onValueChange("email", v || "")}
                    styles={textFieldStyles}
                />
            </div>
        </div>;
    }

    const buttons = () => {
        return <div className="ms-Grid-row pt-20">
            <div className={width6}>
                <DefaultButton onClick={goHome} styles={defaultButtonStyles}>
                    Cancel
                </DefaultButton>
            </div>
            <div className={width6}>
                <PrimaryButton onClick={onSubmit} styles={primaryButtonStyles}>
                    Submit
                </PrimaryButton>
            </div>
        </div>;
    }

    const mainContent = () => {
        return <div id={id} className="pv-10 ph-15" style={styles.middle}>
            <div className="login-form box">
                <div className="ms-Grid ph-0">
                    <AccountOptionView
                        title="Forgot Password"
                        provider={form.isByEmail ? AccountProvider.email : AccountProvider.phone}
                        setProvider={(provider) => {
                            onValueChange("isByEmail", provider === AccountProvider.email)
                        }}
                    />

                    <hr style={{ backgroundColor: "lightgray", height: 1, border: "none" }} />

                    <div className="pt-10 pb-20 ph-15">
                        {!form.isByEmail && phoneView()}
                        {form.isByEmail && emailInput()}
                        {!loading && buttons()}
                        {loading && <div className="pt-10">
                            <ProgressIndicator label="Submitting" description="" />
                        </div>}
                    </div>
                </div>
            </div>
        </div>;
    }

    return <>
        {showConfirm && <>
            <ConfirmAccountView
                form={confirmVM.form}
                errorMessages={confirmVM.errorMessages}
                loading={confirmVM.loading}
                counter={confirmVM.counter}
                onValueChange={confirmVM.setField}
                lastByEmail={confirmVM.lastByEmail}
                resendCode={confirmVM.resendCode}
                confirm={confirmVM.confirm}
                showResetPassword={confirmVM.showResetPassword}
            />
        </>}

        {!showConfirm && mainContent()}
    </>
}

export default ForgotPasswordView;